import { Component, EventEmitter, Input, OnInit, OnChanges, OnDestroy, Output, SimpleChanges } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { COMMUNICATION_SUBTYPES, COMMUNICATION_TYPES } from "src/app/constants/contact";
import { DBL_CLICK_TIMEOUT } from "src/app/constants/edit";
import { Communication } from "src/app/models/communication.model";
import { MasterService } from "src/app/services/master.service";
import { MessageService } from "src/app/services/message.service";
import { formatPhone } from "src/app/utils/format";
import { commIcon } from "src/app/utils/icons";
import { Subscription } from 'rxjs';

@Component({
    selector: 'communications-compact',
    templateUrl: './communications-compact.component.html',
    styleUrls: ['./communications-compact.component.scss'],
    standalone: false
})

export class CommunicationsCompactComponent implements OnInit, OnChanges, OnDestroy {

    /** */
    @Input() contactId: number | null = null

    /** */
    @Input() language: string = 'EN'

    /** */
    @Input() words = {}

    /** */
    @Output() modified: EventEmitter<Object> = new EventEmitter()

    /** A list of communications associated with the current contact. */
    protected communications: Communication[] = []

    /** A form editor for communications. */
    protected commEdit: FormGroup | null = null

    /** Available communication types. */
    protected commTypes: string[] = COMMUNICATION_TYPES
    
    /** Available communication subtypes. */
    protected commSubTypes: string[] = COMMUNICATION_SUBTYPES

    // A debounce timer for click events. Used to wait for 
    // the user to click twice before performing an action.
    private dblClickWatcher: ReturnType<typeof setTimeout> = null

    private subscriptions: Subscription[] = []

    constructor(private master: MasterService, private ms: MessageService, private fb: FormBuilder) {

        this.commEdit = this.fb.group({
            id: new FormControl<number>(0, [Validators.required]),
            contactId: new FormControl<number>(0, [Validators.required]),
            type: new FormControl<string | null>(null, [Validators.required]),
            value: new FormControl<string | null>(null, [Validators.required]),
            subtype: new FormControl<string | null>(null),
            primary: new FormControl<boolean>(false)
        })

    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.commEdit.get('type').valueChanges.subscribe((chosen) => {
                if (chosen === 'phone') {
                    this.commEdit.get('subtype').enable()
                    return
                }

                this.commEdit.get('subtype').setValue(null)
                this.commEdit.get('subtype').disable()
            })
        )
    }

    ngOnDestroy() {
        for (const sub of this.subscriptions) {
            sub.unsubscribe()
        }
        if (this.dblClickWatcher) {
            clearTimeout(this.dblClickWatcher)
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes) {
            return
        }

        if (changes.contactId) {
            this.fetch()
        }
    }

    /**
     * 
     */
    private fetch = (): void => {        
        if (!this.contactId || (this.contactId <= 0)) {
            return
        }
        
        this.master.get(`contacts/${this.contactId}/communications`, (res) => {  
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            if (!res.data.communications) {
                return
            }
            
            this.communications = res.data.communications
        })
    }

    /**
     * Given a communincation type return a material icon to display.
     * @param type A commuinication type to map to an icon.
     */
    protected commIcon = (type: string): string => {
        return commIcon(type)
    }

    /**
     * Given a communincation type return a formatted display string.
     * @param comm 
     */
    protected commDisplay = (comm: Communication): string => {
        switch (comm.type.toLowerCase()) {
            case 'phone':
                return formatPhone(comm.value)
            case 'email':
            default:
                return comm.value
        }
    }

    /**
     * 
     * @param e 
     */
    protected addComm = (e: Event): void => {
        this.cancelComm(e)

        if (!this.communications) {
            this.communications = []
        }

        const comm = new Communication()
        this.commEdit.patchValue({
            id: 0,
            contactId: this.contactId,
            type: 'phone',
            subtype: 'cell',
            primary: false
        })

        this.communications.push({
            ...comm,
            id: 0,
            contactId: this.contactId,
            type: null,
            subtype: null,
            primary: false,
            editing: true
        })
    }

    /**
     * 
     * @param e 
     * @param index
     */
    protected editComm = (e: Event, index: number): void => {
        this.cancelComm(e)
        
        if (!this.communications || (this.communications.length <= 0)) {
            return
        }
        
        if (this.dblClickWatcher !== null) {
            const comm = this.communications[index]
            this.commEdit.patchValue({
                ...comm
            })

            comm.editing = true
        }

        const ctx = this
        this.dblClickWatcher = setTimeout(() => {
            ctx.dblClickWatcher = null
        }, DBL_CLICK_TIMEOUT)
    }

    /**
     * 
     * @param e 
     */
    protected cancelComm = (e: Event) => {
        e.stopPropagation()
        e.preventDefault()

        this.commEdit.reset()
        const index = this.communications.findIndex((item) => item.editing)
        if (index < 0) {
            return
        }

        const comm = this.communications[index]
        if (comm.id && (comm.id > 0)) {
            comm.editing = false
            return
        }

        this.communications.splice(index, 1)
    }

    /**
     * 
     * @param e 
     */
    protected saveComm = (e: Event) => {
        e.stopPropagation()
        e.preventDefault()

        const index = this.communications.findIndex((item) => item.editing)
        if (index < 0) {
            return
        }

        const comm = Object.assign(this.communications[index], this.commEdit.value)
        if (comm.id && (comm.id > 0)) {
            this.modifyComm(comm)
            return
        }

        this.insertComm(comm)
    }

    /**
     * 
     * @param comm 
     */
    private insertComm = (comm: Communication) => {
        if (!comm) {
            return
        }

        const payload = {
            communications: [
                JSON.parse(JSON.stringify(comm))
            ]
        }

        delete(payload.communications[0].id)

        this.master.post(`contacts/${this.contactId}/communications`, payload, (res) => {  
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            comm.editing = false
            this.commEdit.reset()

            if (!res.data || !res.data.communications || (res.data.communications.length <= 0)) {
                return
            }
            
            comm.id = parseInt(res.data.communications[0].id)
            this.modified.emit({communications: [{id: comm.id}]})
        })
    }

    /**
     * 
     * @param comm 
     */
    private modifyComm = (comm: Communication) => {
        if (!comm || !comm.id) {
            return
        }

        const payload = JSON.parse(JSON.stringify(comm))
        delete(payload.id)

        this.master.put(`contacts/${this.contactId}/communications/${comm.id}`, payload, (res) => {  
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }
            
            comm.editing = false
            this.commEdit.reset()
            this.modified.emit({communications: [{id: comm.id}]})
        })
    }

    /**
     * 
     * @param e 
     * @param index
     */
    protected discardComm = (e: Event, index: number): void => {
        if (!this.communications || (this.communications.length <= 0) || (index < 0) || (index >= this.communications.length)) {
            return
        }

        const comm = this.communications[index]
        const commId = comm.id

        this.master.discard(`contacts/${this.contactId}/communications/${commId}`, (res) => {  
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }
            
            this.communications.splice(index, 1)
            this.modified.emit({communications: [{id: comm.id}]})
        })
    }
}